import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GripVertical, Wrench } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { AdminHeading } from "@/components/admin/ui";

function Col({ title, count, accent = "gold", children, onDrop, droppable, testidKey }) {
  const [over, setOver] = useState(false);
  return (
    <div className="min-w-[240px] flex-1"
      onDragOver={droppable ? (e) => { e.preventDefault(); setOver(true); } : undefined}
      onDragLeave={() => setOver(false)}
      onDrop={droppable ? (e) => { setOver(false); onDrop && onDrop(e); } : undefined}>
      <div className={`flex items-center justify-between mb-2 px-1`}>
        <p className="text-[11px] tracking-label uppercase text-muted-foreground">{title}</p>
        <span className={`text-xs ${accent === "red" ? "text-red-300" : accent === "pool" ? "text-pool" : "text-gold"}`}>{count}</span>
      </div>
      <div className={`space-y-2 rounded-lg p-2 min-h-[80px] transition-colors ${over ? "bg-pool/10 border border-pool/40" : "bg-white/[0.02] border border-white/5"}`} data-testid={`col-${testidKey || title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
        {children}
      </div>
    </div>
  );
}

export default function AdminOperationsBoard() {
  const [d, setD] = useState(null);
  const [hk, setHk] = useState([]);
  const [maint, setMaint] = useState([]);
  const [hkStaff, setHkStaff] = useState([]);
  const [maintStaff, setMaintStaff] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [s, h, m, st] = await Promise.all([
        api.get("/admin/ops/summary"), api.get("/admin/housekeeping"),
        api.get("/admin/maintenance"), api.get("/admin/staff"),
      ]);
      setD(s.data); setHk(h.data); setMaint(m.data);
      setHkStaff(st.data.filter((u) => ["housekeeping_staff", "housekeeping_manager"].includes(u.role)));
      setMaintStaff(st.data.filter((u) => ["maintenance", "maintenance_manager"].includes(u.role)));
    } catch { setD({ cards: {} }); }
  };
  useEffect(() => { load(); }, []);
  if (!d) return <Loading />;
  const c = d.cards || {};

  const onDropToStaff = (staffId) => async (e) => {
    const aid = e.dataTransfer.getData("hk-id");
    if (!aid) return;
    try { await api.post("/admin/ops/reassign-housekeeping", { assignment_id: aid, staff_id: staffId }); toast.success("Reassigned."); load(); }
    catch (err) { toast.error(apiErr(err)); load(); }
  };
  const onDropMaintToStaff = (staffId) => async (e) => {
    const mid = e.dataTransfer.getData("maint-id");
    if (!mid) return;
    try { await api.post("/admin/ops/reassign-maintenance", { assignment_id: mid, staff_id: staffId }); toast.success("Reassigned."); load(); }
    catch (err) { toast.error(apiErr(err)); load(); }  // server-validated → visual revert on rejection
  };

  const HkCard = ({ a }) => (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("hk-id", a.id)}
      className="glass rounded-lg p-3 cursor-grab active:cursor-grabbing" data-testid={`board-hk-${a.id}`}>
      <div className="flex items-start gap-2">
        <GripVertical size={14} className="text-muted-foreground mt-0.5 shrink-0" />
        <div><p className="text-xs text-foreground">{a.assignment_type}</p><p className="text-[11px] text-muted-foreground">{a.status} · {a.priority}</p></div>
      </div>
    </div>
  );
  const MaintCard = ({ m }) => (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("maint-id", m.id)}
      className="glass rounded-lg p-3 cursor-grab active:cursor-grabbing" data-testid={`board-maint-${m.id}`}>
      <div className="flex items-start gap-2">
        <Wrench size={13} className="text-muted-foreground mt-0.5 shrink-0" />
        <div><p className="text-xs text-foreground">{m.issue}</p><p className="text-[11px] text-muted-foreground">{m.status} · {m.priority}{m.location ? ` · ${m.location}` : ""}</p></div>
      </div>
    </div>
  );

  const unassigned = hk.filter((a) => !a.assigned_staff_id && a.status !== "Cancelled");
  const CLOSED_M = ["Completed", "Cancelled", "resolved", "closed"];
  const maintOpen = maint.filter((m) => !CLOSED_M.includes(m.status));
  const maintUnassigned = maintOpen.filter((m) => !m.assigned_user_id);

  return (
    <div>
      <AdminHeading title="Operations Board" subtitle="Cross-team readiness · drag work onto a team member" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[["Arrivals", c.arrivals, "pool", "/admin/reservations?filter=arrivals-today"],
          ["Departures", c.departures, "pool", "/admin/reservations?filter=departures-today"],
          ["Rooms Not Ready", c.rooms_not_ready, "red", "/admin/rooms?group=not-ready"],
          ["Rooms Ready", c.rooms_ready, "gold", "/admin/rooms?group=ready"],
          ["Awaiting Inspection", c.hk_awaiting_inspection, "pool", "/admin/housekeeping?status=Inspection Required"],
          ["Reclean Required", c.hk_reclean, "red", "/admin/housekeeping?status=Reclean Required"],
          ["Out of Service", c.out_of_service, "red", "/admin/rooms?status=Out of Service"],
          ["Open Maintenance", c.open_maintenance, "gold", "/admin/maintenance"]].map(([l, v, a, to]) => (
          <button key={l} onClick={() => nav(to)} className="glass rounded-lg p-4 text-left hover:border-pool/40 border border-white/10" data-testid={`board-card-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <p className="text-[10px] tracking-label uppercase text-muted-foreground">{l}</p>
            <p className={`font-serif text-3xl mt-1 ${a === "red" ? "text-red-300" : a === "pool" ? "text-pool" : "text-gold"}`}>{v ?? 0}</p>
          </button>
        ))}
      </div>

      <p className="text-[11px] tracking-label uppercase text-muted-foreground mb-2">Housekeeping</p>
      <div className="flex gap-4 overflow-x-auto pb-4 mb-8" data-testid="ops-kanban">
        <Col title="Unassigned Work" count={unassigned.length} accent="red" testidKey="hk-unassigned">
          {unassigned.map((a) => <HkCard key={a.id} a={a} />)}
          {unassigned.length === 0 && <p className="text-xs text-muted-foreground p-2">None</p>}
        </Col>
        {hkStaff.length === 0 && <Col title="Housekeeping Team" count={0}><p className="text-xs text-muted-foreground p-2">No housekeeping staff yet.</p></Col>}
        {hkStaff.map((s) => {
          const mine = hk.filter((a) => a.assigned_staff_id === s.id && a.status !== "Cancelled");
          return (
            <Col key={s.id} title={[s.first_name, s.last_name].filter(Boolean).join(" ") || s.email} count={mine.length} accent="gold" droppable onDrop={onDropToStaff(s.id)}>
              {mine.map((a) => <HkCard key={a.id} a={a} />)}
              {mine.length === 0 && <p className="text-[11px] text-muted-foreground p-2">Drop work here</p>}
            </Col>
          );
        })}
      </div>

      <p className="text-[11px] tracking-label uppercase text-muted-foreground mb-2">Maintenance</p>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="ops-kanban-maintenance">
        <Col title="Unassigned Issues" count={maintUnassigned.length} accent="red" testidKey="maint-unassigned">
          {maintUnassigned.map((m) => <MaintCard key={m.id} m={m} />)}
          {maintUnassigned.length === 0 && <p className="text-xs text-muted-foreground p-2">None</p>}
        </Col>
        {maintStaff.length === 0 && <Col title="Maintenance Team" count={0}><p className="text-xs text-muted-foreground p-2">No maintenance staff yet.</p></Col>}
        {maintStaff.map((s) => {
          const mine = maintOpen.filter((m) => m.assigned_user_id === s.id);
          return (
            <Col key={s.id} title={[s.first_name, s.last_name].filter(Boolean).join(" ") || s.email} count={mine.length} accent="gold" droppable onDrop={onDropMaintToStaff(s.id)} testidKey={`maint-${s.id}`}>
              {mine.map((m) => <MaintCard key={m.id} m={m} />)}
              {mine.length === 0 && <p className="text-[11px] text-muted-foreground p-2">Drop work here</p>}
            </Col>
          );
        })}
      </div>
    </div>
  );
}
